<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Keyboard, Autoplay, Pagination, Navigation } from "swiper/modules";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faAnglesLeft,
  faAnglesRight,
  faCalendarDays,
  faMountain,
  faRoute,
  faClock,
  faFlag,
  faArrowUp,
  faLocationDot,
  faTimes,
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faUserPlus,
  faUserMinus,
  faVenus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import eventsService from "@/services/eventsService";
import axios from "@/axios";
import Swal from "sweetalert2";

library.add(
  faAnglesLeft,
  faAnglesRight,
  faCalendarDays,
  faMountain,
  faRoute,
  faClock,
  faFlag,
  faArrowUp,
  faLocationDot,
  faTimes,
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faUserPlus,
  faUserMinus,
  faVenus
);

const router = useRouter();
const authStore = useAuthStore();
const events = ref([]);
const isLoading = ref(true);
const error = ref(null);
const hoveredIndex = ref(null);

const showModal = ref(false);
const modalEvent = ref(null);
const isLoadingEvent = ref(false);
const eventError = ref(null);
const isRegistering = ref(false);
const isUnregistering = ref(false);

const isAuthenticated = computed(() => authStore.isAuthenticated);

const modules = [Keyboard, Autoplay, Pagination, Navigation];

// Obtener eventos
const fetchEvents = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const response = await eventsService.getUpcomingEvents();
    if (response.status === "success" && response.data) {
      events.value = response.data;
    } else {
      throw new Error("No se pudieron obtener los eventos");
    }
  } catch (err) {
    console.error("Error al cargar eventos:", err);
    error.value =
      "No pudimos cargar los eventos. Por favor, intenta más tarde.";
  } finally {
    isLoading.value = false;
  }
};

const fetchEventById = async (id) => {
  try {
    isLoadingEvent.value = true;
    eventError.value = null;

    const response = await axios.get(`/events/${id}`);

    if (response.data && response.data.status === "success") {
      modalEvent.value = response.data.data.event;

      if (isAuthenticated.value) {
        modalEvent.value.is_registered = response.data.data.is_registered;
        modalEvent.value.registration_status =
          response.data.data.registration_status;
        modalEvent.value.can_unregister = response.data.data.can_unregister;
      }

      showModal.value = true;
    } else {
      throw new Error("No se pudo obtener la información del evento");
    }
  } catch (err) {
    console.error(`Error al cargar el evento con ID ${id}:`, err);
    eventError.value = "No pudimos cargar la información del evento.";

    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No pudimos cargar la información del evento.",
      confirmButtonText: "Entendido",
    });
  } finally {
    isLoadingEvent.value = false;
  }
};

const showEventDetails = (event) => {
  fetchEventById(event.id);
};

const closeModal = () => {
  showModal.value = false;
  modalEvent.value = null;
};

const registerForEvent = async (eventId) => {
  if (!isAuthenticated.value) {
    router.push(`/login?redirect=/eventos&eventId=${eventId}`);
    return;
  }

  const result = await Swal.fire({
    title: "¿Confirmas tu registro?",
    text: "Te enviaremos una confirmación por correo electrónico.",
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Sí, inscribirme",
    cancelButtonText: "Cancelar",
  });

  if (!result.isConfirmed) return;

  try {
    isRegistering.value = true;

    const response = await axios.post(`/v1/events/${eventId}/register`);

    if (response.data && response.data.status === "success") {
      fetchEventById(eventId);

      Swal.fire({
        title: "¡Registro exitoso!",
        text: response.data.message,
        icon: "success",
        confirmButtonText: "Genial",
        timer: 3000,
      });

      if (showModal.value && modalEvent.value) {
        fetchEventById(modalEvent.value.id);
      }
    }
  } catch (error) {
    console.error("Error al registrarse:", error);

    Swal.fire({
      title: "Error al registrarse",
      text:
        error.response?.data?.message ||
        "Ocurrió un error al procesar tu registro.",
      icon: "error",
      confirmButtonText: "Entendido",
    });
  } finally {
    isRegistering.value = false;
  }
};

const cancelRegistration = async (eventId) => {
  const result = await Swal.fire({
    title: "¿Seguro que deseas cancelar?",
    text: "Esta acción no se puede deshacer.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, cancelar registro",
    cancelButtonText: "Mantener registro",
  });

  if (!result.isConfirmed) return;

  try {
    isUnregistering.value = true;

    const response = await axios.post(`/v1/events/${eventId}/unregister`);

    if (response.data && response.data.status === "success") {
      Swal.fire({
        title: "Registro cancelado",
        text: response.data.message,
        icon: "info",
        confirmButtonText: "Entendido",
        timer: 3000,
      });

      if (showModal.value && modalEvent.value) {
        fetchEventById(modalEvent.value.id);
      }
    }
  } catch (error) {
    console.error("Error al cancelar registro:", error);

    Swal.fire({
      title: "Error al cancelar",
      text:
        error.response?.data?.message ||
        "Ocurrió un error al cancelar tu registro.",
      icon: "error",
      confirmButtonText: "Entendido",
    });
  } finally {
    isUnregistering.value = false;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const formatted = eventsService.formatEventDate(dateStr);
  return `${formatted.components.day} ${formatted.components.monthName} ${formatted.components.year}`;
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const formatted = eventsService.formatEventTime(timeStr);
  return formatted;
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return "/images/default-event.jpg";

  if (imagePath.startsWith("http")) {
    return imagePath;
  }

  return `${import.meta.env.VITE_API_URL}/storage/${imagePath}`;
};

const getCountryFlag = (countryName) => {
  if (!countryName) return "unknown";

  const countryMap = {
    Guatemala: "gt",
    "Costa Rica": "cr",
    Panamá: "pa",
    Nicaragua: "ni",
    Honduras: "hn",
    "El Salvador": "sv",
    "República Dominicana": "do",
  };

  return countryMap[countryName] || "unknown";
};

const handleRegister = (eventId) => {
  if (!isAuthenticated.value) {
    router.push(`/login?redirect=/eventos&eventId=${eventId}`);
    return;
  }

  registerForEvent(eventId);
};

const occupancyPercentage = computed(() => {
  if (!modalEvent.value) return 0;
  return eventsService.getOccupancyPercentage(modalEvent.value);
});

const sortedEvents = computed(() => {
  return eventsService.sortEventsByDate(events.value);
});

const onSlideChange = (swiper) => {
  const activeSlideIndex = swiper.activeIndex;
};

const handleHover = (index) => {
  hoveredIndex.value = index;
};

const isUserRegistered = computed(() => {
  return modalEvent.value?.is_registered || false;
});

const userRegistrationStatus = computed(() => {
  return modalEvent.value?.registration_status || null;
});

const canUserUnregister = computed(() => {
  return modalEvent.value?.can_unregister || false;
});

onMounted(() => {
  fetchEvents();
});
</script>

<template>
  <div class="eventos-container bg-white py-12">
    <div class="max-w-7xl mx-auto px-4">
      <h2 class="text-4xl sm:text-5xl font-bold text-center mb-2 text-gray-900">
        Próximos Eventos
      </h2>
      <p class="text-center mb-10 text-gray-700">
        (El calendario se actualiza con regularidad)
      </p>

      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div class="loader"></div>
      </div>

      <div v-else-if="error" class="text-center py-8 text-red-600">
        {{ error }}
      </div>

      <div v-else-if="sortedEvents.length > 0" class="relative">
        <button class="button-prev z-10">
          <svg
            class="-rotate-90"
            xmlns="http://www.w3.org/2000/svg"
            width="14.974"
            height="17.746"
            viewBox="0 0 14.974 17.746"
          >
            <g
              id="Grupo_131"
              data-name="Grupo 131"
              transform="translate(-7430.874 -2098.879)"
            >
              <g id="Grupo_11" data-name="Grupo 11">
                <g id="Grupo_10" data-name="Grupo 10">
                  <path
                    id="Trazado_14"
                    data-name="Trazado 14"
                    d="M7438.072,2098.959a.558.558,0,0,1,.576,0l6.8,3.744c.18.108.4.252.4.468v4.535c0,.288-.216.5-.4.4l-7.092-4.033-7.09,4.033c-.18.107-.4-.109-.4-.4v-4.535c0-.216.216-.36.4-.468Zm0,8.5a.556.556,0,0,1,.576,0l6.8,3.743c.18.109.4.252.4.469v4.535c0,.288-.216.5-.4.4l-7.092-4.031-7.09,4.031c-.18.108-.4-.108-.4-.4v-4.535c0-.217.216-.36.4-.469Z"
                    fill="#000"
                  />
                </g>
              </g>
            </g>
          </svg>
        </button>

        <swiper
          :spaceBetween="20"
          :modules="modules"
          :loop="true"
          :navigation="{
            nextEl: '.button-next',
            prevEl: '.button-prev',
          }"
          :autoplay="{
            delay: 5000,
            disableOnInteraction: true,
          }"
          :breakpoints="{
            '640': {
              slidesPerView: 1,
              spaceBetween: 20,
            },
            '768': {
              slidesPerView: 2,
              spaceBetween: 30,
            },
            '1024': {
              slidesPerView: 3,
              spaceBetween: 30,
            },
          }"
          :speed="600"
          @slideChange="onSlideChange"
          class="mySwiper"
        >
          <swiper-slide v-for="(event, index) in sortedEvents" :key="event.id">
            <div
              class="event-card bg-white shadow-xl rounded-lg overflow-hidden transition duration-300 h-full"
              :class="{
                inactive: hoveredIndex !== null && hoveredIndex !== index,
              }"
              @mouseover="handleHover(index)"
              @mouseleave="handleHover(null)"
            >
              <div class="event-image h-52 bg-gray-200 relative">
                <img
                  :src="getImageUrl(event.featured_image)"
                  :alt="event.name"
                  class="w-full h-full object-cover"
                />
                <div class="absolute top-3 right-3">
                  <span
                    :class="[
                      'text-xs font-bold px-2 py-1 rounded-full',
                      event.is_fully_booked
                        ? 'bg-red-500 text-white'
                        : 'bg-green-500 text-white',
                    ]"
                  >
                    {{ event.is_fully_booked ? "AGOTADO" : "DISPONIBLE" }}
                  </span>
                </div>
              </div>

              <div class="event-content p-5">
                <h3 class="text-xl font-bold mb-2 text-gray-900 line-clamp-2">
                  {{ event.name }}
                </h3>

                <div class="event-details space-y-2 mb-4">
                  <div class="flex items-center text-gray-700">
                    <font-awesome-icon
                      :icon="['fas', 'calendar-days']"
                      class="w-5 mr-2"
                    />
                    <span>{{ formatDate(event.event_date) }}</span>
                  </div>

                  <div class="flex items-center text-gray-700">
                    <font-awesome-icon
                      :icon="['fas', 'clock']"
                      class="w-5 mr-2"
                    />
                    {{ formatTime(event.start_date) }} - {{ formatTime(event.end_date) }}
                  </div>

                  <div class="flex items-center text-gray-700">
                    <font-awesome-icon
                      :icon="['fas', 'mountain']"
                      class="w-5 mr-2"
                    />
                    <span>Nivel: {{ event.level }}</span>
                  </div>

                  <div class="flex items-center text-gray-700">
                    <font-awesome-icon
                      :icon="['fas', 'flag']"
                      class="w-5 mr-2"
                    />
                    <span>País: {{ event.country.name }}</span>
                  </div>

                  <div class="flex items-center text-gray-700">
                    <font-awesome-icon
                      :icon="['fas', 'route']"
                      class="w-5 mr-2"
                    />
                    <span>Distancia: {{ event.distance }} km</span>
                  </div>

                  <div class="flex items-center text-gray-700">
                    <font-awesome-icon
                      :icon="['fas', 'arrow-up']"
                      class="w-5 mr-2"
                    />
                    <span>Elevación: {{ event.elevation }}</span>
                  </div>
                </div>

                <div class="event-availability mb-4">
                  <div class="flex justify-between items-center mb-1">
                    <span class="text-sm font-medium text-gray-700">
                      {{ event.current_attendees }} /
                      {{ event.max_attendees }} Cupos
                    </span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-2">
                    <div
                      class="bg-blue-600 h-2 rounded-full"
                      :style="`width: ${eventsService.getOccupancyPercentage(
                        event
                      )}%`"
                    ></div>
                  </div>
                </div>

                <div class="event-actions flex space-x-2">
                  <button
                    @click.stop="showEventDetails(event)"
                    class="py-2 px-4 w-1/2 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-lg transition duration-200"
                  >
                    +Información
                  </button>
                  <button
                    @click.stop="handleRegister(event.id)"
                    :disabled="!event.can_register && !isAuthenticated"
                    :class="[
                      'py-2 px-4 w-1/2 font-medium rounded-lg transition duration-200',
                      event.can_register || !isAuthenticated
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-300 text-gray-600 cursor-not-allowed',
                    ]"
                  >
                    {{
                      !isAuthenticated
                        ? "Registrarme"
                        : event.can_register
                        ? "Registrarme"
                        : event.has_waitlist_available
                        ? "Lista de espera"
                        : "Agotado"
                    }}
                  </button>
                </div>
              </div>
            </div>
          </swiper-slide>
        </swiper>

        <button class="button-next z-10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14.974"
            height="17.746"
            viewBox="0 0 14.974 17.746"
            class="rotate-90"
          >
            <g
              id="Grupo_131"
              data-name="Grupo 131"
              transform="translate(-7430.874 -2098.879)"
            >
              <g id="Grupo_11" data-name="Grupo 11">
                <g id="Grupo_10" data-name="Grupo 10">
                  <path
                    id="Trazado_14"
                    data-name="Trazado 14"
                    d="M7438.072,2098.959a.558.558,0,0,1,.576,0l6.8,3.744c.18.108.4.252.4.468v4.535c0,.288-.216.5-.4.4l-7.092-4.033-7.09,4.033c-.18.107-.4-.109-.4-.4v-4.535c0-.216.216-.36.4-.468Zm0,8.5a.556.556,0,0,1,.576,0l6.8,3.743c.18.109.4.252.4.469v4.535c0,.288-.216.5-.4.4l-7.092-4.031-7.09,4.031c-.18.108-.4-.108-.4-.4v-4.535c0-.217.216-.36.4-.469Z"
                    fill="#000"
                  />
                </g>
              </g>
            </g>
          </svg>
        </button>
      </div>
      <div v-else class="text-center py-8 text-gray-600">
        No hay eventos programados por el momento. Vuelve pronto para ver las
        nuevas actividades.
      </div>
    </div>
  </div>

  <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
    <div
      class="modal-container bg-white rounded-lg shadow-xl overflow-hidden max-w-4xl mx-auto"
    >
      <div class="relative">
        <button
          @click="closeModal"
          class="absolute top-4 right-4 z-20 bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
        >
          <font-awesome-icon :icon="['fas', 'times']" class="text-gray-700" />
        </button>

        <div
          class="event-cover-image h-64 md:h-80 bg-gray-200 relative overflow-hidden"
        >
          <img
            :src="getImageUrl(modalEvent?.featured_image)"
            :alt="modalEvent?.name"
            class="w-full h-full object-cover"
          />
          <div class="absolute inset-0 bg-black bg-opacity-40"></div>
          <div class="absolute bottom-0 left-0 right-0 p-6 text-white">
            <h2 class="text-3xl font-bold mb-2 drop-shadow-lg">
              {{ modalEvent?.name }}
            </h2>

            <div class="flex flex-wrap items-center gap-4">
              <div class="flex items-center">
                <font-awesome-icon
                  :icon="['fas', 'calendar-days']"
                  class="mr-2"
                />
                <span>{{ formatDate(modalEvent?.event_date) }}</span>
              </div>

              <div class="flex items-center">
                <font-awesome-icon :icon="['fas', 'flag']" class="mr-2" />
                <span>{{ modalEvent?.country?.name }}</span>
              </div>

              <div v-if="modalEvent?.level" class="flex items-center">
                <font-awesome-icon :icon="['fas', 'mountain']" class="mr-2" />
                <span>Nivel: {{ modalEvent?.level }}</span>
              </div>
              <div v-if="modalEvent?.event_type" class="flex items-center">
               
                <div v-if="modalEvent?.event_type" class="flex items-center">
                <span class="bg-pink-500 text-white px-2 py-1 rounded-full">
                  <font-awesome-icon :icon="['fas', 'venus']" class="mr-2" v-if="modalEvent?.event_type == 'Mujeres'"/>
                  Evento exclusivo para 
                {{ modalEvent?.event_type }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contenido del modal -->
      <div v-if="isLoadingEvent" class="flex justify-center items-center p-10">
        <div class="loader"></div>
      </div>

      <div v-else-if="eventError" class="p-6 text-center text-red-600">
        {{ eventError }}
      </div>

      <div v-else-if="modalEvent" class="p-6 overflow-y-auto">
        <!-- Estado de registro del usuario -->
        <div
          v-if="isAuthenticated && isUserRegistered"
          class="mb-6 p-4 rounded-lg"
          :class="{
            'bg-green-100 border border-green-200':
              userRegistrationStatus === 'registered',
            'bg-yellow-100 border border-yellow-200':
              userRegistrationStatus === 'waitlist',
          }"
        >
          <div class="flex items-center">
            <font-awesome-icon
              :icon="[
                'fas',
                userRegistrationStatus === 'registered'
                  ? 'check-circle'
                  : 'info-circle',
              ]"
              class="text-2xl mr-3"
              :class="{
                'text-green-600': userRegistrationStatus === 'registered',
                'text-yellow-600': userRegistrationStatus === 'waitlist',
              }"
            />
            <div>
              <h4 class="font-bold text-gray-800">
                {{
                  userRegistrationStatus === "registered"
                    ? "¡Estás registrado!"
                    : "Estás en lista de espera"
                }}
              </h4>
              <p class="text-gray-700 text-sm">
                {{
                  userRegistrationStatus === "registered"
                    ? "Tu inscripción a este evento está confirmada."
                    : "Te notificaremos si se libera un cupo."
                }}
              </p>
            </div>
          </div>

          <!-- Botón para cancelar registro -->
          <div v-if="canUserUnregister" class="mt-3 text-right">
            <button
              @click="cancelRegistration(modalEvent.id)"
              class="text-red-600 hover:text-red-800 text-sm font-medium inline-flex items-center"
              :disabled="isUnregistering"
            >
              <font-awesome-icon :icon="['fas', 'user-minus']" class="mr-1" />
              <span>{{
                isUnregistering ? "Cancelando..." : "Cancelar registro"
              }}</span>
            </button>
          </div>
        </div>

        <!-- Detalles del evento organizados en secciones -->
        <div class="space-y-6">
          <!-- Descripción y detalles principales -->
          <div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <!-- Primera columna: Detalles básicos -->
              <div>
                <h4 class="text-lg font-semibold mb-3 text-gray-800">
                  Detalles del evento
                </h4>
                <div class="space-y-2">
                  <div class="flex items-center text-gray-700">
                    <font-awesome-icon
                      :icon="['fas', 'calendar-days']"
                      class="w-5 mr-2"
                    />
                    <span class="font-medium">Fecha:</span>
                    <span class="ml-2">{{
                      formatDate(modalEvent.event_date)
                    }}</span>
                  </div>

                  <div class="flex items-center text-gray-700">
                    <font-awesome-icon
                      :icon="['fas', 'clock']"
                      class="w-5 mr-2"
                    />
                    <span class="font-medium">Horario:</span>
                    <span class="ml-2">{{
                      formatTime(modalEvent.start_date)
                    }} - {{ formatTime(modalEvent.end_date) }}</span>
                  </div>

                  <div
                    v-if="modalEvent.level"
                    class="flex items-center text-gray-700"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'mountain']"
                      class="w-5 mr-2"
                    />
                    <span class="font-medium">Nivel:</span>
                    <span class="ml-2">{{ modalEvent.level }}</span>
                  </div>
                </div>
              </div>

              <!-- Segunda columna: Datos técnicos -->
              <div>
                <h4 class="text-lg font-semibold mb-3 text-gray-800">
                  Información técnica
                </h4>
                <div class="space-y-2">
                  <div
                    v-if="modalEvent.distance"
                    class="flex items-center text-gray-700"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'route']"
                      class="w-5 mr-2"
                    />
                    <span class="font-medium">Distancia:</span>
                    <span class="ml-2">{{ modalEvent.distance }} km</span>
                  </div>

                  <div
                    v-if="modalEvent.elevation !== undefined"
                    class="flex items-center text-gray-700"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'arrow-up']"
                      class="w-5 mr-2"
                    />
                    <span class="font-medium">Elevación:</span>
                    <span class="ml-2">{{ modalEvent.elevation }}</span>
                  </div>

                  <div
                    v-if="modalEvent.address"
                    class="flex items-start text-gray-700"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'location-dot']"
                      class="w-5 mr-2 mt-1"
                    />
                    <div v-if="modalEvent.address" class="space-y-1">
                      <span class="font-medium">Ubicación:</span>
                      <span class="ml-2 break-words">{{
                        modalEvent.address
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Descripción del evento -->
            <div v-if="modalEvent.description" class="mb-6">
              <h4 class="text-lg font-semibold mb-3 text-gray-800">
                Descripción
              </h4>
              <p class="text-gray-700 whitespace-pre-line">
                {{ modalEvent.description }}
              </p>
            </div>

            <!-- Itinerario del evento si existe -->
            <div v-if="modalEvent.itinerary" class="mb-6">
              <h4 class="text-lg font-semibold mb-3 text-gray-800">
                Itinerario
              </h4>
              <p class="text-gray-700 whitespace-pre-line">
                {{ modalEvent.itinerary }}
              </p>
            </div>

            <!-- Disponibilidad y cupos -->
            <div class="mb-6">
              <h4 class="text-lg font-semibold mb-3 text-gray-800">
                Disponibilidad
              </h4>
              <div class="flex justify-between items-center mb-2">
                <span class="text-sm font-medium text-gray-700">
                  {{ modalEvent.current_attendees }} /
                  {{ modalEvent.max_attendees }} Cupos
                </span>
                <span class="text-sm font-medium text-gray-700">
                  {{ occupancyPercentage }}% Ocupado
                </span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  class="bg-blue-600 h-2.5 rounded-full"
                  :style="`width: ${occupancyPercentage}%`"
                ></div>
              </div>

              <!-- Mensaje de disponibilidad -->
              <p
                v-if="
                  modalEvent.has_waitlist_available &&
                  modalEvent.is_fully_booked
                "
                class="mt-2 text-sm text-yellow-600"
              >
                <font-awesome-icon
                  :icon="['fas', 'info-circle']"
                  class="mr-1"
                />
                El evento está lleno, pero puedes unirte a la lista de espera.
              </p>
              <p
                v-else-if="modalEvent.is_fully_booked"
                class="mt-2 text-sm text-red-600"
              >
                <font-awesome-icon
                  :icon="['fas', 'exclamation-circle']"
                  class="mr-1"
                />
                Lo sentimos, este evento está completamente lleno.
              </p>
              <p v-else class="mt-2 text-sm text-green-600">
                <font-awesome-icon
                  :icon="['fas', 'check-circle']"
                  class="mr-1"
                />
                ¡Hay cupos disponibles! Regístrate pronto para asegurar tu
                lugar.
              </p>
            </div>

            <!-- Requisitos o información adicional -->
            <div v-if="modalEvent.requirements" class="mb-6">
              <h4 class="text-lg font-semibold mb-3 text-gray-800">
                Requisitos
              </h4>
              <p class="text-gray-700 whitespace-pre-line">
                {{ modalEvent.requirements }}
              </p>
            </div>
          </div>

          <!-- Botón de acción -->
          <div v-if="!isUserRegistered">
            <button
              v-if="isAuthenticated"
              @click="registerForEvent(modalEvent.id)"
              :disabled="isRegistering || !modalEvent.can_register"
              :class="[
                'w-full py-3 font-bold rounded-lg text-white transition-all',
                isRegistering
                  ? 'bg-gray-400 cursor-wait'
                  : modalEvent.can_register
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : modalEvent.has_waitlist_available
                  ? 'bg-yellow-500 hover:bg-yellow-600'
                  : 'bg-gray-400 cursor-not-allowed',
              ]"
            >
              {{
                isRegistering
                  ? "Procesando..."
                  : modalEvent.can_register
                  ? "Registrarme ahora"
                  : modalEvent.has_waitlist_available
                  ? "Unirme a la lista de espera"
                  : "Evento agotado"
              }}
            </button>

            <button
              v-else
              @click="
                router.push(`/login?redirect=/eventos&eventId=${modalEvent.id}`)
              "
              class="w-full py-3 font-bold rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all"
            >
              Iniciar sesión para registrarme
              
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.loader {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3498db;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.event-card {
  transition: all 0.3s ease;
  height: 100%;
}

.event-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.inactive {
  filter: grayscale(50%);
  opacity: 0.7;
  transition: filter 0.3s, opacity 0.3s;
}

.button-next,
.button-prev {
  display: flex;
  justify-content: center;
  align-items: center;
  height: fit-content;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background-color: white;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.button-prev {
  left: -8%;
}

.button-next {
  right: -8%;
}

.button-next:hover,
.button-prev:hover {
  background-color: #f8f8f8;
  transform: translateY(-50%) scale(1.05);
}

.button-next svg,
.button-prev svg {
  fill: #000;
  width: 30px;
  height: 30px;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 1rem;
  overflow-y: auto;
}

.modal-container {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  animation: modalFadeIn 0.3s ease;
}

.modal-content {
  overflow-y: auto;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive styles */
@media (max-width: 768px) {
  .event-image {
    height: 200px;
  }

  .button-next,
  .button-prev {
    width: 40px;
    height: 40px;
  }

  .button-prev {
    left: -20px;
  }

  .button-next {
    right: -20px;
  }

  .modal-container {
    max-height: 95vh;
    margin: 0;
  }

  .event-cover-image {
    height: 300px;
  }
}

@media (max-width: 640px) {
  .button-next,
  .button-prev {
    display: none;
  }

  .modal-overlay {
    padding: 0;
  }

  .modal-container {
    max-height: 100vh;
    height: 100%;
    border-radius: 0;
  }
}
</style>
